import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
} from '@mui/material'
import { toast } from 'react-toastify'
import { FieldError, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../hooks'
import { RegisterPayload, registerUser } from '../Store/AuthSlice'

function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm()
  const email: string = watch('email')
  const password1: string = watch('password1')
  const password2: string = watch('password2')
  const dispatch = useAppDispatch()
  const { errorMessage, loading } = useAppSelector((state) => state.auth)

  const handleSignUp = async () => {
    const action = await dispatch(
      registerUser({ email, password1, password2 } as RegisterPayload),
    )
    if (registerUser.fulfilled.match(action)) {
      toast.success(t('RegisterSuccess'))
      navigate('/')
    } else {
      toast.error(t('RegisterError'))
    }
  }

  const handleLogin = () => {
    // Redirect to login page
    navigate('/login')
  }

  return (
    <Container maxWidth="xs">
      <Box
        component="form"
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'left',
        }}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSignUp)}
      >
        <h2>{t('Register')}</h2>
        <TextField
          {...register('email', {
            required: t('Required'),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t('InvalidEmail'),
            },
          })}
          required
          label={t('Email Address')}
          variant="outlined"
          fullWidth
          margin="normal"
        />
        {errors.email && (
          <Box sx={{ color: 'error.main' }} data-testid="email-error">
            {(errors.email as FieldError).message}
          </Box>
        )}
        <TextField
          {...register('password1', {
            required: t('Required'),
            minLength: { value: 8, message: t('Password must be at least 8 characters') },
          })}
          required
          label={t('Password')}
          variant="outlined"
          type="password"
          fullWidth
          margin="normal"
        />
        {errors.password1 && (
          <Box sx={{ color: 'error.main' }}>
            {(errors.password1 as FieldError).message}
          </Box>
        )}
        <TextField
          {...register('password2', {
            required: t('Required'),
            validate: value => value === password1 || t('Passwords do not match'),
          })}
          required
          label={t('Confirm Password')}
          variant="outlined"
          type="password"
          fullWidth
          margin="normal"
        />
        {errors.password2 && (
          <Box sx={{ color: 'error.main' }}>
            {(errors.password2 as FieldError).message}
          </Box>
        )}
        <Grid container spacing={2} justifyContent="center" marginTop={2}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              data-testid="register"
              disabled={loading}
            >
              {t('Register')}
              {loading && <CircularProgress size={24} />}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={handleLogin}
              data-testid="login"
            >
              {t('Login')}
            </Button>
          </Grid>
        </Grid>
        {errorMessage && (
          <Box sx={{ color: 'error.main', mt: 2 }}>{errorMessage}</Box>
        )}
      </Box>
    </Container>
  )
}

export default RegisterPage
